import { useMemo } from 'react';

import type { Edge, Node } from '@xyflow/react';

import useV4DatasetDocumentation from 'hooks/data/datasets/V4/documentation/useV4DatasetDocumentation';
import { FlowsInput, FlowsOutput, FlowsType } from 'hooks/data/datasets/V4/flows/types';
import useV4Dataset from 'hooks/data/datasets/V4/useV4Dataset';
import { formatNumericalValue } from 'utilities/numbers';

import { DATASET_NODE_HEIGHT, DATASET_NODE_WIDTH, H_GAP, NODE_WIDTH, V_SPACING, estimateFlowNodeHeight } from './constants';
import { DatasetNodeData } from './DatasetNode';
import { FlowNodeData } from './FlowNode';
import { FlowTabType } from './types';

type UseGraphLayoutParams = {
  datasetId: string;
  inputs: (FlowsInput | FlowsOutput)[];
  outputs: (FlowsInput | FlowsOutput)[];
  flowTabType: FlowTabType;
  onFlowClick: (flowId: string, flowType: FlowsType) => void;
};

type GraphLayout = {
  nodes: Node[];
  edges: Edge[];
};

export const useGraphLayout = ({ datasetId, inputs, outputs, flowTabType, onFlowClick }: UseGraphLayoutParams): GraphLayout => {
  const { data: dataset } = useV4Dataset(datasetId);
  const { data: documentation } = useV4DatasetDocumentation(datasetId);

  const inputFlowType: FlowsType = flowTabType === 'ecosphere' ? 'ecosphere' : 'inputs';
  const outputFlowType: FlowsType = flowTabType === 'ecosphere' ? 'ecosphere' : 'outputs';

  return useMemo(() => {
    const computeLayout = (flows: (FlowsInput | FlowsOutput)[]) => {
      const heights = flows.map(estimateFlowNodeHeight);
      const ys: number[] = [];
      let cursor = 0;
      for (const h of heights) {
        ys.push(cursor);
        cursor += h + V_SPACING;
      }
      const total = heights.length > 0 ? cursor - V_SPACING : 0;
      return { heights, ys, total };
    };

    const inputLayout = computeLayout(inputs);
    const outputLayout = computeLayout(outputs);

    const sideHeight = Math.max(inputLayout.total, outputLayout.total, DATASET_NODE_HEIGHT);
    const datasetY = (sideHeight - DATASET_NODE_HEIGHT) / 2;
    const inputsOffsetY = inputs.length > 0 ? (sideHeight - inputLayout.total) / 2 : datasetY;
    const outputsOffsetY = outputs.length > 0 ? (sideHeight - outputLayout.total) / 2 : datasetY;

    const datasetX = NODE_WIDTH + H_GAP;
    const outputsX = datasetX + DATASET_NODE_WIDTH + H_GAP;

    const nodes: Node[] = [];
    const edges: Edge[] = [];

    const datasetNodeData: DatasetNodeData = {
      name: dataset?.name ?? '…',
      inputCount: inputs.length,
      outputCount: outputs.length,
      geography: dataset?.geography_short,
      timePeriod: documentation?.time_period,
      unit: dataset?.unit,
      activityType: documentation?.type,
      onViewDataset: undefined,
    };

    nodes.push({
      id: 'dataset',
      type: 'datasetNode',
      position: { x: datasetX, y: datasetY },
      data: datasetNodeData as Record<string, unknown>,
      draggable: false,
    });

    inputs.forEach((flow, i) => {
      const nodeId = `input-${flow.id}`;
      const sourceHandleId = `source-input-${i}`;
      const targetHandleId = 'target-center';

      const nodeData: FlowNodeData = {
        flow,
        direction: 'input',
        flowType: inputFlowType,
        handleId: sourceHandleId,
        onViewDataset: () => onFlowClick(flow.id, inputFlowType),
      };

      nodes.push({
        id: nodeId,
        type: 'flowNode',
        position: { x: 0, y: inputsOffsetY + inputLayout.ys[i] },
        data: nodeData as Record<string, unknown>,
        draggable: false,
      });

      edges.push({
        id: `e-input-${flow.id}`,
        source: nodeId,
        sourceHandle: sourceHandleId,
        target: 'dataset',
        targetHandle: targetHandleId,
        type: 'flowEdge',
        label: `${formatNumericalValue(flow.amount)} ${flow.unit}`,
      });
    });

    outputs.forEach((flow, i) => {
      const nodeId = `output-${flow.id}`;
      const sourceHandleId = 'source-center';
      const targetHandleId = `target-output-${i}`;

      const nodeData: FlowNodeData = {
        flow,
        direction: 'output',
        flowType: outputFlowType,
        handleId: targetHandleId,
        onViewDataset: () => onFlowClick(flow.id, outputFlowType),
      };

      nodes.push({
        id: nodeId,
        type: 'flowNode',
        position: { x: outputsX, y: outputsOffsetY + outputLayout.ys[i] },
        data: nodeData as Record<string, unknown>,
        draggable: false,
      });

      edges.push({
        id: `e-output-${flow.id}`,
        source: 'dataset',
        sourceHandle: sourceHandleId,
        target: nodeId,
        targetHandle: targetHandleId,
        type: 'flowEdge',
        label: `${formatNumericalValue(flow.amount)} ${flow.unit}`,
      });
    });

    return { nodes, edges };
  }, [dataset, documentation, inputs, outputs, inputFlowType, outputFlowType, onFlowClick]);
};
